import React, { useCallback, useMemo, useState } from 'react';
import { useFragment, graphql, useMutation, ConnectionHandler } from 'react-relay';
import {
	ConnectionInterface,
	type RecordSourceSelectorProxy,
	type RecordProxy,
} from 'relay-runtime';
import RelayDataID from '@atlassian/relay-data-id';
import type { SmartLinkOption } from '@atlassian/jira-issue-smart-links-popup-edit-view/src/common/types.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import type { GoalsEditViewProps } from '@atlassian/jira-issue-field-goals-editview-common/src/ui/goals/types.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import GoalsEditViewEntryPoint from '@atlassian/jira-issue-field-goals-editview-with-local-updates/src/entrypoint.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	useGoalUrlConstructor,
	getGoalKeyFromUrl,
} from '@atlassian/jira-issue-field-goals-common/src/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { GoalsReadView } from '@atlassian/jira-issue-field-goals-readview-full/src/ui/goals/index.tsx';
import type {
	TownsquareRelationshipInput,
	goals_issueFieldGoals_GoalsField_LinkUnlinkIssueToGoalMutation as LinkUnlinkIssueToGoalMutation,
} from '@atlassian/jira-relay/src/__generated__/goals_issueFieldGoals_GoalsField_LinkUnlinkIssueToGoalMutation.graphql';
import type { goals_issueFieldGoalsInlineEditFull_GoalsInlineEditView_fragmentRef$key as GoalsFragment } from '@atlassian/jira-relay/src/__generated__/goals_issueFieldGoalsInlineEditFull_GoalsInlineEditView_fragmentRef.graphql';
import type { goals_issueFieldGoalsInlineEditFull_GoalsInlineEditViewWithIsEditable_fragmentRef$key as GoalsWithIsEditableFragment } from '@atlassian/jira-relay/src/__generated__/goals_issueFieldGoalsInlineEditFull_GoalsInlineEditViewWithIsEditable_fragmentRef.graphql';
import type {
	GoalsInlineEditViewProps,
	GoalsInlineEditViewWithIsEditableProps,
	ValidNonNullableSelectedGoalNode,
} from './types.tsx';

/** Determine if the updated edit view value is equal to our original value before submitting a mutation. */
const isEqualOption = (a: SmartLinkOption[], b: SmartLinkOption[]) => {
	if (a.length !== b.length) return false;

	const aUrlSet = new Set(a.map((option) => option.url));
	return b.every((option) => aUrlSet.has(option.url));
};

const JIRA_GOAL_TYPENAME = 'JiraGoal';
const JIRA_GOAL_EDGE_TYPENAME = 'JiraGoalEdge';

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `GoalsInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [GoalsInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const GoalsInlineEditViewWithIsEditable = ({
	attributes,
	editViewPopup,
	editViewPopupAlignBlock,
	fragmentRef,
	isEditable,
	isEditing: startWithEditViewOpen = false,
	menuPosition,
	menuPortalTarget,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	readViewFitContainerHeight,
	spacing = 'compact',
	isTruncated,
}: GoalsInlineEditViewWithIsEditableProps) => {
	// #region Relay
	const data = useFragment<GoalsWithIsEditableFragment>(
		graphql`
			fragment goals_issueFieldGoalsInlineEditFull_GoalsInlineEditViewWithIsEditable_fragmentRef on JiraGoalsField {
				__id
				id
				name
				type
				issue @required(action: THROW) {
					id
				}
				...goals_issueFieldGoalsReadviewFull_GoalsReadView
				selectedGoals(first: 1000) {
					__id
					edges {
						node {
							id
							key
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	// for the mutation, only need to worry about the deletion / removal
	// don't worry about insertion as that's handled by the townsquare goal picker

	const [commit] = useMutation<LinkUnlinkIssueToGoalMutation>(graphql`
		mutation goals_issueFieldGoals_GoalsField_LinkUnlinkIssueToGoalMutation(
			$createInput: TownsquareCreateRelationshipsInput!
			$deleteInput: TownsquareDeleteRelationshipsInput!
		) {
			townsquare {
				createRelationships(input: $createInput) @optIn(to: "Townsquare") {
					success
					relationships {
						from
						to
					}
				}
				deleteRelationships(input: $deleteInput) @optIn(to: "Townsquare") {
					success
					relationships {
						from
						to
					}
				}
			}
		}
	`);
	// #endregion Relay

	// #region State
	const constructGoalUrl = useGoalUrlConstructor();

	const { __id, id: uniqueFieldId, name, type: fieldType, selectedGoals, issue } = data;
	const selectedGoalsConnectionId = selectedGoals?.__id;

	const initialValue = useMemo((): SmartLinkOption[] => {
		const nodes: ValidNonNullableSelectedGoalNode[] =
			selectedGoals?.edges
				?.map((edge) => edge?.node)
				?.filter((node): node is ValidNonNullableSelectedGoalNode => Boolean(node?.key)) ?? [];

		return nodes.map((node) => ({
			id: node.id,
			url: constructGoalUrl(node.key),
		}));
	}, [constructGoalUrl, selectedGoals?.edges]);

	const [updatedValue, setUpdatedValue] = useState<SmartLinkOption[]>(initialValue);

	const { overriding } = useInlineEditFieldInjections();
	const { overrideLabel, overrideIsEditable } = overriding;

	const fieldName = useMemo(() => overrideLabel(name), [overrideLabel, name]);

	const isFieldEditable = useMemo(
		() => overrideIsEditable(isEditable),
		[overrideIsEditable, isEditable],
	);

	// #region Action
	const updater = useCallback(
		(store: RecordSourceSelectorProxy, value: SmartLinkOption[]) => {
			const connection = selectedGoalsConnectionId ? store.get(selectedGoalsConnectionId) : null;
			if (!connection) {
				return;
			}

			const { EDGES, NODE } = ConnectionInterface.get();
			const edges = connection.getLinkedRecords(EDGES) ?? [];
			const edgesByNodeId: Record<string, RecordProxy> = {};
			edges.forEach((edge) => {
				const nodeId = edge.getLinkedRecord(NODE)?.getValue('id');
				if (typeof nodeId === 'string') {
					edgesByNodeId[nodeId] = edge;
				}
			});
			const nextEdges: RecordProxy[] = [];
			value.forEach(({ id, url }) => {
				if (id && id in edgesByNodeId) {
					nextEdges.push(edgesByNodeId[id]);
				} else {
					const nodeRelayDataId = RelayDataID({ id }, JIRA_GOAL_TYPENAME);
					const key = getGoalKeyFromUrl(url);

					if (nodeRelayDataId) {
						let node = store.get(nodeRelayDataId);
						if (!node) {
							node = store.create(nodeRelayDataId, JIRA_GOAL_TYPENAME);
							node.setValue(nodeRelayDataId, 'id');
						}
						if (key) {
							node.setValue(key, 'key');
						}

						const edge = ConnectionHandler.createEdge(
							store,
							connection,
							node,
							JIRA_GOAL_EDGE_TYPENAME,
						);
						nextEdges.push(edge);
					}
				}
			});
			connection.setLinkedRecords(nextEdges, EDGES);
		},
		[selectedGoalsConnectionId],
	);

	const handleSubmit = useCallback(
		(value: SmartLinkOption[], { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(value);

			const initialIds = initialValue.map(({ id }) => id).filter(Boolean);
			const initialIdSet = new Set(initialIds);
			const updatedIds = value.map(({ id }) => id).filter(Boolean);
			const updatedIdSet = new Set(updatedIds);

			const deletedRelationships: TownsquareRelationshipInput[] = initialIds
				.filter((id) => !updatedIdSet.has(id))
				.map((id) => ({
					from: issue.id,
					to: id,
				}));

			const createdRelationships: TownsquareRelationshipInput[] = updatedIds
				.filter((id) => !initialIdSet.has(id))
				.map((id) => ({
					from: issue.id,
					to: id,
				}));

			commit({
				variables: {
					createInput: {
						relationships: createdRelationships,
					},
					deleteInput: {
						relationships: deletedRelationships,
					},
				},
				updater: (store) => updater(store, value),
				optimisticUpdater: (store) => updater(store, value),
				onCompleted(mutationData) {
					if (
						mutationData.townsquare?.createRelationships?.success &&
						mutationData.townsquare?.deleteRelationships?.success
					) {
						onSuccess();
					} else {
						onFail();
					}
				},
				onError(error: Error) {
					onFail(error);
				},
				optimisticResponse: {
					townsquare: {
						createRelationships: {
							success: true,
							relationships: createdRelationships,
						},
						deleteRelationships: {
							success: true,
							relationships: deletedRelationships,
						},
					},
				},
			});
		},
		[onSubmit, initialValue, commit, issue.id, updater],
	);

	const {
		invalidMessage,
		isEditing,
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
	} = useFieldInlineEditActions({
		attributes,
		fieldId: uniqueFieldId,
		fieldName,
		fieldType,
		initialValue,
		isValueEqual: isEqualOption,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
	});
	// #endregion Action

	// #region Read view
	const renderReadView = () => <GoalsReadView fragmentRef={data} isTruncated={isTruncated} />;
	// #endregion

	// #region Edit view
	const getEditViewProps = (
		fieldProps: ValidationFieldProps,
	): Omit<GoalsEditViewProps, 'query'> => ({
		...fieldProps,
		issueAri: issue.id,
		initialIsOpen: true,
		value: updatedValue,
		menuPosition,
		menuPortalTarget,
		onChange: handleChange,
		spacing,
	});

	const cloudId = useCloudId();
	const editViewEntryPointParams = useMemo(
		() => ({
			cloudId,
			issueAri: issue.id,
		}),
		[cloudId, issue.id],
	);

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewEntryPoint={GoalsEditViewEntryPoint}
			editViewEntryPointParams={editViewEntryPointParams}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hideActionButtons
		/>
	);
	// #endregion Edit View
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [GoalsInlineEditViewProps](./types.tsx)
 */
export const GoalsInlineEditView = ({ fragmentRef, ...props }: GoalsInlineEditViewProps) => {
	// #region Relay
	const data = useFragment<GoalsFragment>(
		graphql`
			fragment goals_issueFieldGoalsInlineEditFull_GoalsInlineEditView_fragmentRef on JiraGoalsField {
				...goals_issueFieldGoalsInlineEditFull_GoalsInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);
	// #endregion

	return (
		<GoalsInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
